import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import ApiError from '../../entities/api-error/ApiError'
import { AuthAction } from '../store/actions/auth/AuthAction'
import { logout } from '../store/actions/auth/Logout'
import {
  IdempotencyKeyRegistry,
  buildRequestSignature,
  isMutatingMethod,
} from './idempotency'
import { resolveApiBaseUrl } from './resolveApiBaseUrl'

export interface IApiConfig {
  apiUrl?: string
}

interface IdempotentRequestConfig extends AxiosRequestConfig {
  idempotencySignature?: string
}

const idempotencyRegistry = new IdempotencyKeyRegistry()

export class Api {
  client: AxiosInstance

  constructor(config: IApiConfig) {
    if (!config.apiUrl) {
      throw new Error('apiUrl cannot be empty')
    }

    this.client = axios.create({
      baseURL: config.apiUrl,
    })

    this._setup()
  }

  protected _setup() {
    this.client.interceptors.request.use(async request => {
      const config = request as IdempotentRequestConfig

      if (isMutatingMethod(config.method)) {
        const signature = buildRequestSignature(config)
        config.idempotencySignature = signature

        if (!config.headers) {
          config.headers = {}
        }
        config.headers['Idempotency-Key'] = idempotencyRegistry.acquire(signature)
      }

      return config
    })

    this.client.interceptors.response.use(
      response => {
        idempotencyRegistry.settleSuccess((response.config as IdempotentRequestConfig).idempotencySignature)
        return response
      },
      error => {
        idempotencyRegistry.settleFailure((error.config as IdempotentRequestConfig | undefined)?.idempotencySignature)

        if (error.response && error.response.status === 401) {
          // Dynamic, not a static top-level import: `services/store` transitively
          // imports every epics module, several of which eagerly construct a data
          // handler that reads this file's `api` export at module-eval time (e.g.
          // epics/auth/index.ts, epics/dashboard/index.ts). A static `import {
          // store } from '../store'` here closes that into a real ESM cycle —
          // store -> epics -> a data handler -> this file -> store — and depending
          // on how webpack's module concatenation happens to order the cycle, a
          // data handler can end up reading `api` (or `store`) before its
          // declaration initializes, throwing "Cannot access '<x>' before
          // initialization" on every cold start. Reproduced on-device twice, with
          // two different variables, after unrelated changes elsewhere shifted
          // that ordering. `store` is only ever needed here, well after boot, in
          // response to an actual 401 — a dynamic import defers the read entirely
          // until then, when the module graph has long finished resolving,
          // removing this file's participation in the cycle instead of chasing
          // whichever eager singleton the next ordering shift happens to break.
          void import('../store').then(({ store }) => {
            store.dispatch<AuthAction>(logout())
          })
        }

        // TODO replace with translation
        // const networkFallbackMessage = intlProvider.intl.formatMessage(messages.networkError, { appName: config.appName})
        const networkFallbackMessage = 'Network error'
        const status = (error.response && error.response.status) ? error.response.status : 0
        const message = error.response?.data?.message || error.message

        const httpError = new ApiError({
          message: status === 0 ? networkFallbackMessage : message,
          status,
        })

        return Promise.reject(httpError)
      }
    )
  }
}

export const api = new Api({
  apiUrl: resolveApiBaseUrl(),
})
